import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import type { ChangeOrder, ProposalBuilderData } from '../types';
import { db } from '../utils/firebase';
import { subscribeChangeOrdersForDeal } from '../utils/changeOrders';
import { jobNodeProposalBudget, jobNodeQbActual } from '../utils/jobPurchaseBudget';
import type { PurchaseJobPlBucket } from '../types/purchasing';
import { useJobPurchasePlActualInputs } from './useJobPurchasePlActualInputs';

export function useJobPurchaseBudget(dealId: string | undefined, plBucket: PurchaseJobPlBucket | '' | undefined) {
  const [proposal, setProposal] = useState<ProposalBuilderData | null>(null);
  const [proposalReady, setProposalReady] = useState(!dealId);
  const [changeOrders, setChangeOrders] = useState<ChangeOrder[]>([]);
  const plActual = useJobPurchasePlActualInputs(dealId);

  useEffect(() => {
    if (!dealId) {
      setProposal(null);
      setProposalReady(true);
      return;
    }
    setProposalReady(false);
    return onSnapshot(
      doc(db, 'proposalBuilders', dealId),
      snap => {
        setProposal(snap.exists() ? ({ id: snap.id, ...(snap.data() as object) } as ProposalBuilderData) : null);
        setProposalReady(true);
      },
      () => {
        setProposal(null);
        setProposalReady(true);
      },
    );
  }, [dealId]);

  useEffect(() => {
    if (!dealId) {
      setChangeOrders([]);
      return;
    }
    return subscribeChangeOrdersForDeal(dealId, setChangeOrders);
  }, [dealId]);

  const bucket = plBucket || undefined;
  const budgetUsd = dealId && bucket ? jobNodeProposalBudget(proposal, changeOrders, bucket) : 0;
  const actualUsd =
    dealId && bucket
      ? jobNodeQbActual(plActual.expenses, dealId, bucket, {
          overrides: plActual.overrides,
          designerInvoiceJobCash: plActual.designerInvoiceJobCash,
        })
      : 0;
  return {
    budgetUsd,
    actualUsd,
    proposal,
    changeOrders,
    proposalReady,
    loaded: !dealId || proposalReady,
  };
}
